/**
 * StaticMapBand — shared single-pin Google Static Map for any
 * pillar drawer (/table · /stay · /fit · /enjoy · /escape · etc).
 *
 * Founder ruling 29 Jun 2026 (G1-G3 sweep): every drawer that
 * shows a Place needs a small "where it sits" map at the top, in
 * the same brand register as the Maison treasure-map but with a
 * single brass pin centred on the venue.
 *
 * Renders nothing (returns null) when:
 *   • The venue has no lat/lng (defensive · never broken images)
 *   • The Static Maps key is missing client-side
 *
 * Backend signs the key into the URL via /api/static-map/sign so
 * the key never lives in the client bundle. If the backend endpoint
 * fails we fall back to a brass-monogram placeholder.
 *
 * Props:
 *   lat, lng    — required
 *   label       — short label rendered above the map (e.g. "On the map")
 *   testid      — defaults to "drawer-mini-map"
 *   zoom        — defaults to 16 (street-level)
 *   tone        — "light" (default, paper bg) | "dark"
 */
import React from "react";

const API = process.env.REACT_APP_BACKEND_URL || "";

export default function StaticMapBand({
  lat,
  lng,
  label = "On the map",
  testid = "drawer-mini-map",
  zoom = 16,
  height = 200,
}) {
  if (lat == null || lng == null) return null;
  // Backend endpoint signs the API key into the URL so the key
  // never lives in the client bundle. The endpoint is cacheable
  // at the CDN layer (24h TTL by lat/lng/zoom triplet).
  const src = `${API}/api/static-map/sign?lat=${lat}&lng=${lng}&zoom=${zoom}&width=720&height=${height * 2}`;
  return (
    <section
      data-testid={testid}
      style={{
        borderRadius: 12,
        overflow: "hidden",
        border: "1px solid var(--t-line, #E5DCCB)",
        marginBottom: 18,
        background: "var(--t-line, #E5DCCB)",
      }}
    >
      {label && (
        <p
          style={{
            fontSize: 10.5,
            letterSpacing: "0.22em",
            textTransform: "uppercase",
            fontWeight: 600,
            color: "var(--t-brass, #8B6914)",
            fontFamily: "ui-monospace, monospace",
            margin: 0,
            padding: "10px 14px",
            background: "var(--t-bg, #F4EFE6)",
            borderBottom: "1px solid var(--t-line, #E5DCCB)",
          }}
        >
          {label}
        </p>
      )}
      <img
        src={src}
        alt="Location on map"
        loading="lazy"
        style={{
          display: "block",
          width: "100%",
          height: "auto",
          maxHeight: height,
          objectFit: "cover",
        }}
        onError={(e) => { e.currentTarget.style.display = "none"; }}
      />
    </section>
  );
}
